import { Fragment, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import type { CaseOverride, RestaurantSummary } from '../api/schemas';
import { buildMapUrl } from '../lib/mapUrl';
import { toDisplayCase } from '../lib/displayCase';
import { GradeBadge } from './GradeBadge';

interface Props {
  results: RestaurantSummary[];
  onSelect: (permitNumber: string) => void;
  selectedPermit: string | null;
  isLoading: boolean;
  /** Detail content rendered inline directly beneath the selected establishment. */
  renderDetail?: ((permitNumber: string) => ReactNode) | undefined;
  /** DB-sourced case overrides from the lookups endpoint. */
  overrides?: CaseOverride[];
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
}

export function ResultsList({
  results,
  onSelect,
  selectedPermit,
  isLoading,
  renderDetail,
  overrides = [],
}: Props) {
  if (isLoading && results.length === 0) {
    return (
      <p className="px-4 py-12 text-center text-sm text-gray-500">Loading…</p>
    );
  }
  if (results.length === 0) {
    return (
      <p className="px-4 py-12 text-center text-sm text-gray-500">
        No results. Try fewer filters.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-gray-200">
      {results.map((r) => {
        const selected = selectedPermit === r.permit_number;
        return (
          <Fragment key={r.permit_number}>
            <li className={`flex items-stretch ${selected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
              <button
                type="button"
                onClick={() => onSelect(r.permit_number)}
                aria-expanded={selected}
                className="flex-1 min-w-0 text-left px-4 py-4 flex items-start justify-between gap-3 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-base font-medium text-gray-900 truncate">
                    {toDisplayCase(r.restaurant_name, overrides, 'name')}
                  </p>
                  <p className="mt-1 text-sm text-gray-600 truncate">
                    {toDisplayCase(r.address, overrides, 'address')}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 flex items-center gap-2">
                    <GradeBadge code={r.current_grade} size="sm" />
                    <span>
                      <span className="font-medium text-gray-700">{r.current_demerits ?? 0}</span>{' '}
                      demerit{(r.current_demerits ?? 0) === 1 ? '' : 's'}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Last inspection: {fmtDate(r.date_current)}
                  </p>
                </div>
                <ChevronRight
                  className={`flex-shrink-0 w-5 h-5 text-gray-400 transition-transform ${
                    selected ? 'rotate-90 text-snhd-primary' : ''
                  }`}
                  aria-hidden
                />
              </button>
              <a
                href={buildMapUrl(r)}
                target="_blank"
                rel="noreferrer noopener"
                onClick={(e) => e.stopPropagation()}
                className="flex-shrink-0 flex items-center px-4 text-sm font-medium text-snhd-primary hover:text-snhd-primary-hover hover:bg-blue-50/60 border-l border-gray-200"
              >
                Map
              </a>
            </li>
            {selected && renderDetail && (
              <li className="bg-gray-50">{renderDetail(r.permit_number)}</li>
            )}
          </Fragment>
        );
      })}
    </ul>
  );
}
